import { formatMoney } from '@theme/utilities';
import { CartUpdatedEvent } from '@theme/events';
import { availableSizes, compareAmount, discountPercent, findVariant, optionIndexes, variantSize } from '@theme/direct-buy-match';

class DirectBuy extends HTMLElement {
  #config = { i18n: {}, packOption: 'Pack', sizeOption: 'Taille' };
  #packCounts = [1, 3, 5];
  #cards = new Map();
  #rendering = false;

  connectedCallback() {
    const configNode = this.querySelector('[data-ref="config"]');

    try {
      const config = JSON.parse((configNode?.value || configNode?.textContent || '').trim());
      this.#config = { ...this.#config, ...config };
    } catch {
      this.#config = { ...this.#config, i18n: {} };
    }

    const counts = String(this.#config.packCounts || '1,3,5')
      .split(',')
      .map((value) => Number(String(value).trim()))
      .filter((value) => Number.isInteger(value) && value > 0);

    this.#packCounts = counts.length ? counts : [1];

    this.addEventListener('change', this.#onChange);
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onGalleryKey);
    this.addEventListener('submit', this.#onSubmit);

    for (const card of this.querySelectorAll('[data-ref="card"]')) {
      this.#initCard(card);
    }
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onGalleryKey);
    this.removeEventListener('submit', this.#onSubmit);
    this.#cards.clear();
  }

  #initCard(card) {
    const checkedTier = card.querySelector('[data-ref="tier-input"]:checked');
    const checkedColor = card.querySelector('.swatch-input:checked')?.closest('[data-ref="color"]');
    const initialColor = checkedColor || card.querySelector('[data-ref="color"]');

    this.#cards.set(card, {
      variants: [],
      packIndex: -1,
      sizeIndex: -1,
      chosenCount: Number(checkedTier?.value || this.#packCounts[0]),
      currentVariant: null,
      color: initialColor,
    });

    card.querySelector('[data-ref="gallery"]')?.addEventListener('scroll', () => this.#syncGallery(card), { passive: true });

    for (const color of card.querySelectorAll('[data-ref="color"]')) {
      const input = color.querySelector('.swatch-input');
      if (input && color.dataset.label) input.setAttribute('aria-label', color.dataset.label);
    }

    this.#setColor(card, initialColor);
  }

  #onClick = (event) => {
    const control = event.target.closest(
      '[data-ref="prev"], [data-ref="next"], [data-ref="dots"] button, [data-ref="thumb"], [data-ref="thumbs-prev"], [data-ref="thumbs-next"]',
    );
    if (!control || !this.contains(control)) return;

    const card = control.closest('[data-ref="card"]');
    const gallery = card?.querySelector('[data-ref="gallery"]');
    if (!card || !gallery) return;

    if (control.matches('[data-ref="thumbs-prev"], [data-ref="thumbs-next"]')) {
      this.#scrollThumbs(card, control.matches('[data-ref="thumbs-next"]') ? 1 : -1);
      return;
    }

    const index = this.#slideIndex(gallery);
    let nextIndex = index;

    if (control.matches('[data-ref="prev"]')) nextIndex = index - 1;
    if (control.matches('[data-ref="next"]')) nextIndex = index + 1;
    if (control.matches('[data-ref="dots"] button, [data-ref="thumb"]')) {
      nextIndex = [...control.parentElement.children].indexOf(control);
    }

    const slide = gallery.children[nextIndex];
    if (!slide) return;

    gallery.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
  };

  #onGalleryKey = (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

    const gallery = event.target.closest('[data-ref="gallery"]');
    if (!gallery) return;

    event.preventDefault();
    const index = this.#slideIndex(gallery);
    const nextIndex = event.key === 'ArrowRight' ? index + 1 : index - 1;
    const slide = gallery.children[nextIndex];
    if (!slide) return;

    gallery.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
  };

  #slideIndex(gallery) {
    return [...gallery.children].reduce(
      (best, slide, index) => {
        const distance = Math.abs(slide.offsetLeft - gallery.scrollLeft);
        return distance < best.distance ? { index, distance } : best;
      },
      { index: 0, distance: Infinity },
    ).index;
  }

  #scrollThumbs(card, direction) {
    const thumbs = card.querySelector('[data-ref="thumbs"]');
    if (!thumbs) return;

    const vertical = getComputedStyle(thumbs).flexDirection === 'column';
    thumbs.scrollBy({
      left: vertical ? 0 : direction * (thumbs.clientWidth || 80),
      top: vertical ? direction * (thumbs.clientHeight || 80) : 0,
      behavior: 'smooth',
    });
  }

  #onChange = (event) => {
    const card = event.target.closest('[data-ref="card"]');
    if (!card || !this.#cards.has(card)) return;

    this.#clearFeedback(card);

    if (event.target.matches('[data-ref="size"]')) {
      this.#updateCard(card);
      return;
    }

    if (event.target.matches('[data-ref="tier-input"]')) {
      if (!event.target.checked || event.target.disabled) return;
      this.#cards.get(card).chosenCount = Number(event.target.value);
      this.#updateCard(card);
      return;
    }

    if (!event.target.matches('.swatch-input')) return;

    this.#setColor(card, event.target.closest('[data-ref="color"]'));
  };

  #onSubmit = async (event) => {
    const form = event.target.closest?.('[data-ref="form"]') || event.target;
    if (!form?.matches?.('[data-ref="form"]') || !this.contains(form)) return;

    event.preventDefault();

    const card = form.closest('[data-ref="card"]');
    const state = card ? this.#cards.get(card) : null;
    const button = card?.querySelector('[data-ref="submit"]');
    if (!card || !state || !button || button.classList.contains('is-loading')) return;

    if (!state.currentVariant?.id) {
      this.#feedback(card, this.#config.i18n.selectOptions);
      const sizeField = card.querySelector('[data-ref="size-field"]');
      if (sizeField && !sizeField.hidden) card.querySelector('[data-ref="size"]')?.focus();
      return;
    }

    button.classList.add('is-loading');
    button.disabled = true;
    this.#clearFeedback(card);

    const sections = ['auto-gifts'];
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer?.dataset.sectionId) sections.push(cartDrawer.dataset.sectionId);

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(state.currentVariant.id), quantity: 1 }],
          sections: sections.join(','),
        }),
      });
      const data = await response.json();

      if (!response.ok || (data.status && data.status >= 400)) {
        throw new Error(data.description || data.message || this.#config.i18n.error);
      }

      if (window.location.pathname.includes('/cart')) {
        window.location.assign(Theme.routes.cart_url);
        return;
      }

      const currentItemCount = document.querySelector('cart-icon')?.getCurrentItemCount();

      document.dispatchEvent(
        new CartUpdatedEvent(
          {},
          {
            itemCount: currentItemCount != null ? currentItemCount + 1 : undefined,
            source: 'direct-buy',
            sections: data.sections,
          },
        ),
      );

      const behavior = document.querySelector('cart-icon')?.addToCartBehavior || 'open_cart';
      if (behavior === 'notification') {
        document.dispatchEvent(
          new CustomEvent('toast:open', {
            detail: { type: 'success', message: Theme.translations.addToCartNotificationMessage },
          }),
        );
      }
    } catch (error) {
      const message = error.message || this.#config.i18n.error;
      this.#feedback(card, message);
      document.dispatchEvent(new CustomEvent('toast:open', { detail: { type: 'error', message } }));
    } finally {
      button.classList.remove('is-loading');
      this.#updateCard(card);
    }
  };

  #setColor(card, color) {
    const state = this.#cards.get(card);
    if (!state || !color) return;

    try {
      state.variants = JSON.parse(color.querySelector('[data-ref="variants"]').textContent);
      const optionNames = JSON.parse(color.querySelector('[data-ref="options"]').textContent);
      const indexes = optionIndexes(optionNames, this.#config.packOption, this.#config.sizeOption);
      state.packIndex = indexes.packIndex;
      state.sizeIndex = indexes.sizeIndex;
    } catch {
      this.#feedback(card, this.#config.i18n.loadError);
      return;
    }

    const sizeSelect = card.querySelector('[data-ref="size"]');
    const sizeField = card.querySelector('[data-ref="size-field"]');
    const previousSize = sizeSelect?.value || '';
    const sizes = availableSizes(state.variants, state, this.#packCounts);

    if (sizeField) sizeField.hidden = state.sizeIndex === -1;

    if (sizeSelect && state.sizeIndex !== -1) {
      sizeSelect.replaceChildren();

      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = this.#config.i18n.chooseSize;
      sizeSelect.append(placeholder);

      for (const size of sizes) {
        const option = document.createElement('option');
        option.value = size;
        option.textContent = size;
        option.disabled = !this.#packCounts.some((count) => this.#variant(state, count, size));
        sizeSelect.append(option);
      }

      sizeSelect.value = sizes.includes(previousSize) ? previousSize : '';
    }

    const colorName = card.querySelector('[data-ref="color-name"]');
    if (colorName) colorName.textContent = color.dataset.label || '';

    if (state.color !== color) {
      this.#renderGallery(card, color);
      state.color = color;
    }

    this.#syncGallery(card);
    this.#updateCard(card);
  }

  #images(color) {
    try {
      const images = JSON.parse(color?.querySelector('[data-ref="images"]')?.textContent || '[]');
      return Array.isArray(images) ? images : [];
    } catch {
      return [];
    }
  }

  #renderGallery(card, color) {
    const gallery = card.querySelector('[data-ref="gallery"]');
    if (!gallery) return;

    const slides = this.#images(color).map((image) => {
      const slide = document.createElement('div');
      slide.className = 'direct-buy__slide';
      const img = document.createElement('img');
      img.className = 'direct-buy__image';
      img.alt = image.alt || color.dataset.label || '';
      img.src = image.src;
      img.loading = 'lazy';
      slide.append(img);
      return slide;
    });

    gallery.replaceChildren(...slides);
    gallery.scrollLeft = 0;
    this.#renderThumbs(card, this.#images(color));
  }

  #renderThumbs(card, images) {
    const thumbs = card.querySelector('[data-ref="thumbs"]');
    if (!thumbs) return;

    delete thumbs.dataset.current;
    thumbs.replaceChildren(
      ...images.map((image, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'direct-buy__thumb';
        button.dataset.ref = 'thumb';
        button.setAttribute('aria-label', (this.#config.i18n.image || '').replace('[index]', String(index + 1)));

        const img = document.createElement('img');
        img.className = 'direct-buy__thumb-image';
        img.alt = '';
        img.src = image.src;
        img.loading = 'lazy';
        button.append(img);
        return button;
      }),
    );
  }

  #syncGallery(card) {
    const gallery = card.querySelector('[data-ref="gallery"]');
    if (!gallery) return;

    const count = gallery.children.length;
    const several = count > 1;
    const index = this.#slideIndex(gallery);
    const label = (position) => (this.#config.i18n.image || '').replace('[index]', String(position));

    const dots = card.querySelector('[data-ref="dots"]');
    if (dots) {
      if (dots.children.length !== count) {
        dots.replaceChildren();

        for (let dotIndex = 0; dotIndex < count; dotIndex += 1) {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'direct-buy__dot';
          button.setAttribute('aria-label', label(dotIndex + 1));
          dots.append(button);
        }
      }

      dots.hidden = !several;
      [...dots.children].forEach((dot, dotIndex) => {
        if (dotIndex === index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    const prev = card.querySelector('[data-ref="prev"]');
    const next = card.querySelector('[data-ref="next"]');
    if (prev) {
      prev.hidden = !several;
      prev.disabled = index <= 0;
    }
    if (next) {
      next.hidden = !several;
      next.disabled = index >= count - 1;
    }

    const wrap = card.querySelector('[data-ref="thumbs-wrap"]');
    const thumbs = card.querySelector('[data-ref="thumbs"]');
    if (wrap) wrap.hidden = !several;
    if (!thumbs || thumbs.dataset.current === String(index)) return;

    thumbs.dataset.current = String(index);
    [...thumbs.children].forEach((thumb, thumbIndex) => {
      if (thumbIndex === index) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });

    const thumb = thumbs.children[index];
    if (!thumb) return;

    const vertical = getComputedStyle(thumbs).flexDirection === 'column';
    thumbs.scrollTo({
      left: vertical ? 0 : thumb.offsetLeft,
      top: vertical ? thumb.offsetTop : 0,
      behavior: 'smooth',
    });
  }

  #updateCard(card) {
    if (this.#rendering) return;

    const state = this.#cards.get(card);
    if (!state) return;

    this.#rendering = true;

    try {
      const sizeSelect = card.querySelector('[data-ref="size"]');
      const chosenSize = state.sizeIndex === -1 ? '' : sizeSelect?.value || '';
      const indexes = { packIndex: state.packIndex, sizeIndex: state.sizeIndex };

      state.currentVariant = chosenSize || state.sizeIndex === -1 ? this.#variant(state, state.chosenCount, chosenSize) : null;

      if (chosenSize && !state.currentVariant && state.sizeIndex !== -1) {
        const alternative = this.#packCounts.find((count) => this.#variant(state, count, chosenSize));

        if (alternative) {
          state.chosenCount = alternative;
          const input = card.querySelector(`[data-ref="tier-input"][value="${alternative}"]`);
          if (input) input.checked = true;
          state.currentVariant = this.#variant(state, alternative, chosenSize);
        }
      }

      const fallback = findVariant(state.variants, { ...indexes, count: 1, matchSize: false, onlyAvailable: true });
      const reference = chosenSize
        ? findVariant(state.variants, { ...indexes, count: 1, size: chosenSize, onlyAvailable: false })
        : fallback;

      for (const tier of card.querySelectorAll('[data-ref="tier"]')) {
        const input = tier.querySelector('[data-ref="tier-input"]');
        const count = Number(input.value);
        const preview = this.#preview(state, count, chosenSize);
        const previewSize = preview && state.sizeIndex !== -1 ? variantSize(preview, state.sizeIndex) : chosenSize;
        const unit = preview
          ? findVariant(state.variants, { ...indexes, count: 1, size: previewSize, onlyAvailable: false, matchSize: state.sizeIndex === -1 || Boolean(previewSize) }) || reference
          : null;
        const compare = compareAmount(preview, count, unit);

        tier.hidden = state.packIndex === -1 && count !== 1;
        input.disabled = !preview;
        tier.classList.toggle('is-disabled', !preview);

        const total = tier.querySelector('[data-ref="tier-total"]');
        if (total) {
          total.textContent = '';
          if (preview) total.innerHTML = formatMoney(preview.price);
          else total.textContent = this.#config.i18n.unavailable;
        }

        this.#setMoney(tier.querySelector('[data-ref="tier-compare"]'), compare);

        const badge = tier.querySelector('[data-ref="tier-discount"]');
        const reduction = preview && compare ? discountPercent(preview.price, compare) : 0;
        if (badge) {
          const discountLabel = this.#config.i18n.discount || '';
          badge.hidden = reduction <= 0;
          badge.textContent = reduction > 0 ? discountLabel.replace('[percent]', String(reduction)) : '';
        }
      }

      const shown = state.currentVariant || reference || fallback;
      const shownCount = state.currentVariant ? state.chosenCount : 1;
      const shownSize = shown && state.sizeIndex !== -1 ? variantSize(shown, state.sizeIndex) : chosenSize;
      const unitForShown =
        state.currentVariant && state.sizeIndex !== -1
          ? findVariant(state.variants, { ...indexes, count: 1, size: shownSize, onlyAvailable: false })
          : reference;

      const price = card.querySelector('[data-ref="price"]');
      if (price) {
        price.textContent = '';
        if (shown) price.innerHTML = formatMoney(shown.price);
        else price.textContent = this.#config.i18n.unavailable;
      }

      this.#setMoney(card.querySelector('[data-ref="price-compare"]'), compareAmount(shown, shownCount, unitForShown));

      const variantField = card.querySelector('[data-ref="variant-id"]');
      if (variantField) variantField.value = state.currentVariant ? String(state.currentVariant.id) : '';

      const button = card.querySelector('[data-ref="submit"]');
      const label = card.querySelector('[data-ref="submit-label"]');
      if (button) button.disabled = !state.currentVariant;
      if (label) label.textContent = state.currentVariant ? this.#config.i18n.addToCart : this.#config.i18n.chooseSize;
    } finally {
      this.#rendering = false;
    }
  }

  #variant(state, count, size) {
    return findVariant(state.variants, {
      packIndex: state.packIndex,
      sizeIndex: state.sizeIndex,
      count,
      size,
      matchSize: state.sizeIndex === -1 || Boolean(size),
    });
  }

  #preview(state, count, chosenSize) {
    return findVariant(state.variants, {
      packIndex: state.packIndex,
      sizeIndex: state.sizeIndex,
      count,
      size: chosenSize,
      matchSize: state.sizeIndex === -1 || Boolean(chosenSize),
    });
  }

  #setMoney(element, cents) {
    if (!element) return;
    const show = Number.isFinite(cents) && cents > 0;
    element.hidden = !show;
    element.innerHTML = show ? formatMoney(cents) : '';
  }

  #feedback(card, message) {
    const feedback = card.querySelector('[data-ref="feedback"]');
    if (feedback) feedback.textContent = message || '';
  }

  #clearFeedback(card) {
    this.#feedback(card, '');
  }
}

if (!customElements.get('direct-buy')) {
  customElements.define('direct-buy', DirectBuy);
}
